/* ==========================================================================
   Vai Dai — "5 Reasons" landing page
   Vanilla JS, no dependencies. The theme ships jQuery but nothing here needs
   it, and this file must not touch shared theme JS.

   Everything degrades: with JS off the page still renders, the FAQ answers are
   all visible (good for SEO and for the visitor), and the anchor CTAs work.
   Only the two add-to-cart controls require JS, which is unavoidable for the
   AJAX Cart API.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-vid-lp]');
  if (!root) return;

  /* ------------------------------------------------------------------------
     Ad-click parameters
     Anchor CTAs on this page are hash-only, so they never touch the query
     string. The one-time path DOES navigate, so it has to carry the tracking
     parameters forward itself or the checkout loses attribution.

     An allowlist rather than forwarding location.search wholesale: this URL is
     attacker-influencable, and blindly appending it to a checkout URL would
     forward anything an ad could put there.
     --------------------------------------------------------------------- */
  var TRACKING_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
    'fbclid', 'gclid', 'ttclid', 'twclid', 'msclkid', 'igshid', 'epik', 'irclickid',
    'ref', 'referrer'
  ];

  function trackingQuery() {
    var current = new URLSearchParams(window.location.search);
    var out = new URLSearchParams();
    TRACKING_PARAMS.forEach(function (key) {
      var value = current.get(key);
      if (value) out.set(key, value);
    });
    var str = out.toString();
    return str ? '?' + str : '';
  }

  function withTracking(path) {
    return path + trackingQuery();
  }

  /* ------------------------------------------------------------------------
     Analytics
     The theme itself emits no add-to-cart events — Meta, GA4 and TikTok are
     all injected through Shopify's pixel layer. So publish to the platform's
     own channels and to a DOM event, each guarded, and add no new scripts.

     Every call carries a distinct `id` so the page's internals are readable
     afterwards: buybox-subscribe, buybox-onetime, hero, header, sticky,
     reason-N, faq-open, ugc-play.
     --------------------------------------------------------------------- */
  function track(eventName, payload) {
    var detail = Object.assign({ page: 'vid-5-reasons' }, payload || {});

    try {
      document.dispatchEvent(new CustomEvent('vid-lp:' + eventName, { detail: detail }));
    } catch (e) { /* CustomEvent unsupported — nothing else depends on this */ }

    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: 'vid_lp_' + eventName }, detail));
      }
    } catch (e) {}

    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('vid_lp_' + eventName, detail);
      }
    } catch (e) {}
  }

  /* ------------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  function addToCart(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (response.ok) return response.json();
      // /cart/add.js answers 422 with a human-readable `description` when an
      // inventory or quantity rule blocks the add. Surface that, not "failed".
      return response.json().then(
        function (err) { throw new Error((err && (err.description || err.message)) || 'Could not add to cart.'); },
        function () { throw new Error('Could not add to cart.'); }
      );
    });
  }

  /*
    Opening the cart drawer on this store is not what the theme's own code
    suggests, so this is worth spelling out.

    The theme defines window.openCartDrawer / window.updateCartDrawer in
    snippets/cart-drawer-js.liquid, but that snippet starts with
    `if (!document.querySelector('.enj-minicart-ajax')) return;` and the markup
    it looks for is never rendered — {% include 'minicart' %} is commented out
    in layout/theme.liquid. So both globals are undefined store-wide, including
    on the PDP, where the call to them is a silent no-op.

    The drawer that actually exists is Recharge Storefront Experiences. It is
    configured (RechargeStorefrontConfig.cart_drawer...advanced.selectors) with
      cart_trigger: ".js-call-minicart, [data-recharge-cart-trigger]"
      add_to_cart_enabled: false
    — i.e. it opens on a click of a trigger element and does NOT open itself on
    add to cart. Clicking such an element is therefore the supported way in,
    and a synthetic .click() works. Verified live on this store.

    The theme globals are still called first, so this page follows the rest of
    the store the moment the minicart markup is ever restored.
  */
  // The header badge is rendered server-side, so it goes stale the moment an
  // AJAX add succeeds. Re-read the cart rather than incrementing locally, so
  // the number stays right even if the cart changed in another tab.
  function refreshCartCount() {
    var badges = document.querySelectorAll('[data-vid-cart-count]');
    if (!badges.length) return;
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        badges.forEach(function (b) {
          b.textContent = cart.item_count;
          b.hidden = cart.item_count === 0;
        });
      })
      .catch(function () { /* the badge just stays as rendered */ });
  }

  function openCartDrawer() {
    try {
      if (typeof window.updateCartDrawer === 'function') window.updateCartDrawer();
    } catch (e) {}
    try {
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
        return true;
      }
    } catch (e) {}

    // Nudge Recharge to re-read the cart before it paints the panel.
    try {
      if (window.RechargePubSub && typeof window.RechargePubSub.publish === 'function') {
        window.RechargePubSub.publish('cart:refresh');
      }
    } catch (e) {}

    var trigger = document.querySelector('[data-vid-cart-trigger], [data-recharge-cart-trigger], .js-call-minicart');
    if (trigger) {
      trigger.click();
      return true;
    }
    return false;
  }

  /* ======================================================================
     Buy box
     ====================================================================== */
  (function buyBox() {
    var box = root.querySelector('[data-vid-buybox]');
    if (!box) return;

    var flavors;
    try {
      flavors = JSON.parse(box.getAttribute('data-flavors') || '[]');
    } catch (e) {
      return;
    }
    if (!flavors.length) return;

    var radios = Array.prototype.slice.call(box.querySelectorAll('[data-vid-flavor]'));
    var errorEl = box.querySelector('[data-vid-error]');
    var subscribeBtn = box.querySelector('[data-vid-atc="subscribe"]');
    var oneTimeBtn = box.querySelector('[data-vid-atc="onetime"]');

    // One in-flight guard for the whole buy box. A double click, or a click on
    // the other control while the first is still going, must not add twice.
    var busy = false;

    // Whether the ingredients disclosure was left open, so switching flavor
    // keeps it open rather than collapsing it under the reader.
    var openIngredients = false;

    function current() {
      var checked = radios.filter(function (r) { return r.checked; })[0] || radios[0];
      var id = checked ? checked.value : null;
      return flavors.filter(function (f) { return f.id === id; })[0] || flavors[0];
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.classList.add('is-visible');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.classList.remove('is-visible');
    }

    function setBusy(button, state, busyLabel) {
      busy = state;
      [subscribeBtn, oneTimeBtn].forEach(function (b) {
        if (!b) return;
        b.disabled = state;
        b.setAttribute('aria-disabled', state ? 'true' : 'false');
      });
      if (!button) return;
      if (state) {
        button.dataset.vidRestore = button.innerHTML;
        button.textContent = busyLabel;
      } else if (button.dataset.vidRestore) {
        button.innerHTML = button.dataset.vidRestore;
        delete button.dataset.vidRestore;
      }
    }

    // Repaint every price node from the selected flavor's precomputed,
    // Liquid-formatted strings. No client-side money formatting: the server
    // already did it in the shop's own currency format.
    function render() {
      var f = current();
      box.querySelectorAll('[data-vid-price]').forEach(function (el) {
        var key = el.getAttribute('data-vid-price');
        if (f.display && f.display[key]) el.textContent = f.display[key];
      });

      if (subscribeBtn) {
        subscribeBtn.disabled = !f.available;
        subscribeBtn.setAttribute('aria-disabled', f.available ? 'false' : 'true');
      }

      // Mirror the selection onto the sticky bar so it quotes the same price.
      document.querySelectorAll('[data-vid-sticky-price]').forEach(function (el) {
        if (f.display && f.display.perBottle) el.textContent = f.display.perBottle;
      });

      // Swap the gallery and the ingredients list to match the flavor. Both
      // are server-rendered for every flavor and simply toggled, so there is
      // no fetch, no re-render and nothing to get out of sync with the price.
      box.ownerDocument.querySelectorAll('[data-vid-gallery]').forEach(function (g) {
        g.hidden = g.getAttribute('data-vid-gallery') !== f.id;
      });
      document.querySelectorAll('[data-vid-ingredients]').forEach(function (d) {
        var match = d.getAttribute('data-vid-ingredients') === f.id;
        // Carry the open/closed state across a flavor switch rather than
        // snapping shut on someone who had deliberately opened it.
        if (!match && d.open) openIngredients = true;
        if (!match && !d.open) openIngredients = false;
        d.hidden = !match;
        if (match) d.open = openIngredients;
      });

      clearError();
    }

    function handleAdd(kind, button) {
      if (busy) return;
      var f = current();

      var item = {
        id: f.variantId,
        quantity: kind === 'subscribe' ? f.subQuantity : f.oneTimeQuantity
      };
      // A one-time purchase must carry NO selling_plan key at all — sending
      // null still creates a subscription line on some Shopify versions.
      if (kind === 'subscribe') item.selling_plan = f.sellingPlanId;

      clearError();
      setBusy(button, true, kind === 'subscribe' ? 'Adding…' : 'Adding…');

      track('add_to_cart', {
        id: kind === 'subscribe' ? 'buybox-subscribe' : 'buybox-onetime',
        flavor: f.label,
        handle: f.handle,
        variantId: f.variantId,
        quantity: item.quantity,
        sellingPlanId: item.selling_plan || null,
        valueCents: kind === 'subscribe' ? f.price.subTotal : f.price.oneTimeTotal
      });

      addToCart({ items: [item] })
        .then(function () {
          refreshCartCount();
          if (kind === 'subscribe') {
            setBusy(button, false);
            if (!openCartDrawer()) {
              // No drawer available — fall through rather than stranding them
              // on a page that looks like nothing happened.
              window.location.href = withTracking('/cart');
            }
          } else {
            // The one-time path deliberately leaves the page, so it has to
            // carry the ad parameters with it.
            window.location.href = withTracking('/checkout');
          }
        })
        .catch(function (err) {
          setBusy(button, false);
          showError(err.message || 'Something went wrong. Please try again.');
          track('add_to_cart_error', {
            id: kind === 'subscribe' ? 'buybox-subscribe' : 'buybox-onetime',
            message: err.message || 'unknown'
          });
        });
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        render();
        track('flavor_change', { id: 'buybox-flavor', flavor: current().label });
      });
    });

    if (subscribeBtn) {
      subscribeBtn.addEventListener('click', function () { handleAdd('subscribe', subscribeBtn); });
    }
    if (oneTimeBtn) {
      oneTimeBtn.addEventListener('click', function () { handleAdd('onetime', oneTimeBtn); });
    }

    // Thumbnails scroll the snap track rather than swapping a src, so the
    // main image never has to re-decode and swiping stays in sync with them.
    root.querySelectorAll('[data-vid-gallery]').forEach(function (gallery) {
      var track = gallery.querySelector('[data-vid-gallery-track]');
      var thumbs = [].slice.call(gallery.querySelectorAll('[data-vid-gallery-thumb]'));
      if (!track || !thumbs.length) return;

      function setActive(i) {
        thumbs.forEach(function (t, n) {
          t.classList.toggle('is-active', n === i);
          t.setAttribute('aria-current', n === i ? 'true' : 'false');
        });
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          var slide = track.children[i];
          if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
          setActive(i);
        });
      });

      // Keep the thumbnails honest when the track is swiped directly.
      var tick;
      track.addEventListener('scroll', function () {
        clearTimeout(tick);
        tick = setTimeout(function () {
          var i = Math.round(track.scrollLeft / (track.clientWidth || 1));
          setActive(Math.max(0, Math.min(thumbs.length - 1, i)));
        }, 90);
      }, { passive: true });

      setActive(0);
    });

    render();
  }());

  /* ======================================================================
     Mobile sticky ATC
     Shown once the hero has scrolled away, hidden whenever the buy box is on
     screen so the two never overlap. IntersectionObserver only — no scroll
     handler, so this costs nothing on the main thread while scrolling.
     ====================================================================== */
  (function stickyBar() {
    var bar = root.querySelector('[data-vid-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;

    var hero = root.querySelector('.vid-lp-hero');
    var offer = document.getElementById('offer');

    var heroGone = false;
    var offerVisible = false;

    function sync() {
      var show = heroGone && !offerVisible;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if (hero) {
      new IntersectionObserver(function (entries) {
        heroGone = !entries[0].isIntersecting;
        sync();
      }, { threshold: 0 }).observe(hero);
    } else {
      heroGone = true;
    }

    if (offer) {
      new IntersectionObserver(function (entries) {
        offerVisible = entries[0].isIntersecting;
        sync();
      }, { threshold: 0 }).observe(offer);
    }

    sync();
  }());

  /* ======================================================================
     FAQ accordion
     Markup ships expanded so the answers are in the DOM and visible without
     JS. This collapses all but the first on init, then toggles.
     ====================================================================== */
  (function faq() {
    var items = root.querySelectorAll('[data-vid-faq-trigger]');
    if (!items.length) return;

    items.forEach(function (btn, index) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;

      var open = index === 0;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;

      btn.addEventListener('click', function () {
        var isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        panel.hidden = isOpen;
        if (!isOpen) {
          track('faq_open', { id: 'faq-' + (index + 1), question: btn.textContent.trim().slice(0, 80) });
        }
      });
    });
  }());

  /* ======================================================================
     Header cart button
     Recharge binds this element itself (it matches the cart_trigger selector
     it is configured with) and opens its own drawer. This only covers the
     case where Recharge failed to load, so the button still goes somewhere
     instead of doing nothing — and carries the ad parameters with it.
     ====================================================================== */
  (function cartButton() {
    var btn = root.querySelector('[data-vid-cart-button]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var rechargeReady = !!(window.RechargeStorefrontConfig || window.RechargePubSub);
      if (rechargeReady) return;   // Recharge has this
      window.location.href = withTracking('/cart');
    });
  }());

  /* ======================================================================
     Anchor CTA instrumentation
     Every in-page CTA is a plain hash link — no JS needed for the scroll, and
     no chance of dropping a query parameter. This only reports the click.
     ====================================================================== */
  (function anchors() {
    root.querySelectorAll('[data-vid-cta]').forEach(function (el) {
      if (el.hasAttribute('data-vid-atc')) return; // cart buttons report their own
      el.addEventListener('click', function () {
        track('cta_click', { id: el.getAttribute('data-vid-cta'), label: (el.textContent || '').trim().slice(0, 60) });
      });
    });
  }());

}());
